import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { simpleFetch } from 'simple-typed-fetch';
import { keyValueStorage, logger, WalletsNames } from '@orionprotocol/common';
import { SupportedChainId } from '@orionprotocol/sdk';
import { isChainInCurrentEnvironment } from '@orionprotocol/utils';
import { createOkxCookie } from '@orionprotocol/wallet-api';
import { useToast } from '@orionprotocol/ui';
import { ethers } from 'ethers';
import BigNumber from 'bignumber.js';

import { useWallet } from '../entities/wallets';
import {
  CONNECT_WALLET_CANCELLED,
  CONNECT_WALLET_ERROR,
  useAnalytics,
  WALLET_CONNECTED,
} from '../shared/analytics';
import { useLumia } from '../shared/context';
import { useEnv } from '../shared/hooks';
import {
  getAppChainId,
  getIsCustomConfig,
  getWalletAddress,
  getWalletConnectWalletName,
  getWalletType,
} from '../shared/redux/selectors';
import {
  appSlice,
  governanceSlice,
  lumiaSlice,
  userSlice,
} from '../shared/redux/slices';
import { usePageAvailableNetworks } from '../shared/api';

import { App } from './App';

export function Preloader(): JSX.Element {
  // This checks if user has OKX Wallet extension installed
  // and adds cookie that allows to use Metamask alongside OKX
  createOkxCookie();

  const dispatch = useDispatch();
  const appChainId = useSelector(getAppChainId);
  const { envs, env } = useEnv();
  const { sendEvent } = useAnalytics();

  const isCustomConfig = useSelector(getIsCustomConfig);

  const { availableUnits } = useLumia();

  const walletType = useSelector(getWalletType);
  const walletConnectWalletName = useSelector(getWalletConnectWalletName);
  const walletAddress = useSelector(getWalletAddress);

  const { showToast } = useToast();

  useWallet({
    onConnectSuccess: ({ chainId }, isAutoconnect) => {
      sendEvent(WALLET_CONNECTED, {
        type: walletType,
        walletId: chainId,
        ...(walletConnectWalletName !== undefined &&
          walletType === WalletsNames.WalletConnect
          ? { walletConnectWalletName }
          : {}),
      });

      if (chainId !== undefined && !isAutoconnect) {
        dispatch(appSlice.actions.setAppNetwork(chainId.toString()));
      }
    },
    onConnectError: () => {
      sendEvent(CONNECT_WALLET_ERROR, { type: walletType });
    },
    onConnectCancelled: () => {
      sendEvent(CONNECT_WALLET_CANCELLED, { type: walletType });
    },
  });

  const updateOrionNetworksData = useCallback(() => {
    Object.values(availableUnits).forEach(
      ({ chainId: chId, blockchainService, aggregator, indexer }) => {
        if (
          env === undefined ||
          !isChainInCurrentEnvironment(envs, env, chId)
        ) {
          return;
        }

        dispatch(
          governanceSlice.actions.setChainInfoIsLoading({
            chainId: chId,
          }),
        );

        if (indexer !== undefined) {
          simpleFetch(indexer.getEnvironment)()
            .then(({ result }) => {
              dispatch(
                governanceSlice.actions.setGovernanceEnvironment({
                  chainId: chId,
                  data: result,
                }),
              );

              if (result.OrionVoting !== undefined) {
                dispatch(
                  governanceSlice.actions.setChainInfo({
                    chainId: chId,
                    data: { isChainSupported: true },
                  }),
                );

                simpleFetch(indexer.listPoolV2)(walletAddress)
                  .then(({ result }) => {
                    dispatch(
                      governanceSlice.actions.setPools({
                        chainId: chId,
                        data: result,
                        appChainId,
                      }),
                    );
                  })
                  .catch((e) => {
                    dispatch(governanceSlice.actions.setIsPoolsLoading(false));
                    logger.error(e);
                  });
              } else {
                dispatch(
                  governanceSlice.actions.setChainInfo({
                    chainId: chId,
                    data: { isChainSupported: false },
                  }),
                );
              }
            })
            .catch((e) => {
              dispatch(
                governanceSlice.actions.setChainInfo({
                  chainId: chId,
                  data: { isChainSupported: false },
                }),
              );

              logger.error(e);
            });
        }

        simpleFetch(blockchainService.getInfo)()
          .then((data) => {
            // Key creator
            dispatch(
              lumiaSlice.actions.setInfo({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);

        simpleFetch(blockchainService.getPoolsConfig)()
          .then((data) => {
            dispatch(
              lumiaSlice.actions.setPoolsConfig({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);

        simpleFetch(blockchainService.getPricesWithQuoteAsset)()
          .then((data) => {
            dispatch(
              lumiaSlice.actions.setPricesWithQuoteAsset({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);

        simpleFetch(blockchainService.getGasPriceWei)()
          .then((dataRaw) => {
            const gwei = ethers.formatUnits(dataRaw, 'gwei');
            const data = new BigNumber(gwei).toString();

            dispatch(
              lumiaSlice.actions.setGasPrice({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);

        simpleFetch(blockchainService.getPoolsInfo)()
          .then((data) => {
            dispatch(
              lumiaSlice.actions.setPoolsInfo({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);

        simpleFetch(aggregator.getPairsList)('spot')
          .then((data) => {
            dispatch(
              lumiaSlice.actions.setPairs({
                chainId: chId,
                data,
              }),
            );
          })
          .catch(logger.error);
      },
    );
  }, [appChainId, availableUnits, dispatch, env, envs, walletAddress]);

  useEffect(() => {
    const isAnalyticsForced = keyValueStorage.getItem('analyticsEnabled');

    if (isAnalyticsForced === 'true') {
      showToast({
        text: 'You have force enabled sending analytics',
        // iconType: 'info',
      });
    } else if (isAnalyticsForced === 'false') {
      showToast({
        text: 'You have force disabled sending analytics',
        //     iconType: 'info',
      });
    }
  }, [showToast]);

  useEffect(() => {
    if (isCustomConfig !== undefined) {
      updateOrionNetworksData();
    }

    if (window.location.hash.length > 0) {
      if (
        appChainId !== SupportedChainId.MAINNET &&
        window.location.hash.includes('ethereum')
      ) {
        dispatch(userSlice.actions.disconnectWallet());
        dispatch(appSlice.actions.setAppNetwork(SupportedChainId.MAINNET));
      } else if (
        appChainId !== SupportedChainId.BSC &&
        window.location.hash.includes('bsc')
      ) {
        dispatch(userSlice.actions.disconnectWallet());
        dispatch(appSlice.actions.setAppNetwork(SupportedChainId.BSC));
      }
      window.location.hash = '';
    }
  }, [dispatch, appChainId, updateOrionNetworksData, isCustomConfig]);

  useEffect(() => {
    const mobileAppChainId = keyValueStorage.getItem('mobileAppChainId');

    if (mobileAppChainId !== undefined) {
      dispatch(appSlice.actions.setAppNetwork(mobileAppChainId));
    }
  }, [dispatch]);

  const { supportedChainIds } = usePageAvailableNetworks();
  useEffect(() => {
    if (
      supportedChainIds.length > 0 &&
      appChainId !== undefined &&
      !supportedChainIds.includes(appChainId)
    ) {
      dispatch(appSlice.actions.setAppNetwork(supportedChainIds[0]));
    }
  }, [supportedChainIds, appChainId, dispatch])

  return <App />;
}
